import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import CompBanner from "@/components/CompBanner";
import { Button } from "@/components/ui/button";
import { VideoCameraIcon as VideoCamera, ClockIcon as Clock, CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, HourglassIcon as Hourglass, CoinsIcon as Coins, ArrowRightIcon as ArrowRight, ChatCircleIcon as ChatCircle, CalendarIcon as Calendar, CalendarPlusIcon as CalendarPlus } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { isIOS, webOrigin } from "@/lib/nativeBridge";
import IosPurchaseNotice from "@/components/IosPurchaseNotice";
import { usePublicPrices } from "@/lib/usePublicPrices";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError, thumbUrl } from "@/lib/utils-app";
import EmptyState from "@/components/EmptyState";
import { RowListSkeleton } from "@/components/SkeletonList";
import VideoTourPayDialog from "@/components/VideoTourPayDialog";

/**
 * `/tours` — both buyer (outgoing) and seller (incoming) tours in one
 * dashboard. Card status drives the action button.
 */
const STATUS_META = {
  pending_seller:   { label: "Awaiting seller", icon: Hourglass, color: "#C9A227", bg: "#FBF7EC" },
  counter_proposed: { label: "New times proposed", icon: Calendar, color: "#3F4A8A", bg: "#EEF0FB" },
  pending_payment:  { label: "Awaiting payment", icon: Coins,    color: "#C1272D", bg: "#FDEEEE" },
  paid_scheduled:   { label: "Scheduled",        icon: CheckCircle, color: "#0F8043", bg: "#EEFBEC" },
  completed:        { label: "Completed",        icon: CheckCircle, color: "#3F4A8A", bg: "#EEF0FB" },
  declined:         { label: "Declined",         icon: XCircle,  color: "#6B6B6B", bg: "#F4F4F4" },
  cancelled:        { label: "Cancelled",        icon: XCircle,  color: "#6B6B6B", bg: "#F4F4F4" },
};

export default function MyVideoTours() {
  const { user } = useAuth();
  const { prices } = usePublicPrices();
  const tourPrice = prices("video_tour", "$35");
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [payDialogTour, setPayDialogTour] = useState(null);
  const tourPriceUsd = Number(String(tourPrice).replace(/[^\d.]/g, "")) || 35;
  const navigate = useNavigate();

  const load = () =>
    api.get("/video-tours/mine")
      .then((r) => setTours(r.data || []))
      .catch(() => setTours([]))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const respond = async (id, decision, declineReason = "") => {
    setBusyId(id);
    try {
      await api.post(`/video-tours/${id}/respond`, {
        decision,
        decline_reason: declineReason,
      });
      toast.success(decision === "accept" ? "Tour accepted — buyer will pay to confirm." : "Tour declined.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const pay = async (id) => {
    setBusyId(id);
    try {
      const r = await api.post("/billing/checkout", {
        package_id: "video_tour",
        origin_url: webOrigin(),
        tour_id: id,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      // admin freebie path
      toast.success("Tour confirmed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this tour?")) return;
    setBusyId(id);
    try {
      await api.post(`/video-tours/${id}/cancel`);
      toast.success("Tour cancelled.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const join = (id) => navigate(`/tours/${id}/room`);

  const proposeAlternatives = async (tourId) => {
    const slot1 = window.prompt(
      "Propose alternative time #1 (paste a local datetime, e.g. 2026-06-12 14:30):"
    );
    if (!slot1) return;
    const slot2 = window.prompt(
      "Optional second alternative (leave blank to skip):"
    );
    const slot3 = window.prompt(
      "Optional third alternative (leave blank to skip):"
    );
    const message = window.prompt(
      "Optional note for the buyer (e.g. 'mornings work better for me'):"
    );
    const toIso = (s) => {
      if (!s || !s.trim()) return null;
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d.toISOString();
    };
    const proposed_times = [slot1, slot2, slot3].map(toIso).filter(Boolean);
    if (!proposed_times.length) {
      toast.error("No valid times — please retry.");
      return;
    }
    setBusyId(tourId);
    try {
      await api.post(`/video-tours/${tourId}/counter`, {
        proposed_times,
        message: message || "",
      });
      toast.success("Alternatives sent. The buyer can pick one from their tours page.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const acceptCounter = async (tourId, chosenIso) => {
    setBusyId(tourId);
    try {
      await api.post(`/video-tours/${tourId}/counter-accept`, {
        chosen_at: chosenIso,
      });
      toast.success("Time locked in. Pay the booking fee to confirm.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const downloadIcs = async (tourId) => {
    try {
      const res = await api.get(`/video-tours/${tourId}/calendar.ics`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `armstrong-k9-tour-${tourId.slice(0, 8)}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const googleCalendarLink = (t) => {
    const when = t.scheduled_at;
    if (!when) return null;
    const start = new Date(when);
    if (isNaN(start.getTime())) return null;
    const end = new Date(start.getTime() + 15 * 60 * 1000);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d+/, "");
    const dogLabel = t.dog?.name || "kennel";
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: `Armstrong K9 — Video tour of ${dogLabel}`,
      dates: `${fmt(start)}/${fmt(end)}`,
      details: `Live 15-minute video tour booked through Armstrong K9 Registry.\nJoin: ${webOrigin()}/tours/${t.id}/room`,
    });
    return `https://calendar.google.com/calendar/u/0/r/eventedit?${params.toString()}`;
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-2 md:px-0" data-testid="my-video-tours">
        <div className="flex items-center gap-3 mb-2">
          <VideoCamera size={28} weight="duotone" className="text-crimson" />
          <h1 className="font-spectral text-4xl tracking-tight">Video tours</h1>
        </div>
        <p className="text-[14px] text-[#6B6B6B] max-w-2xl mb-8 leading-relaxed">
          Live 15-minute video tours powered by Daily.co. Both buyer and
          seller views appear here — incoming requests for kennels you
          own, outgoing requests for dogs you're considering buying.
        </p>

        <CompBanner className="mb-6" />

        {loading && <RowListSkeleton count={3} />}

        {!loading && tours.length === 0 && (
          <EmptyState
            icon={<VideoCamera size={28} weight="duotone" />}
            title="No video tours yet"
            description="Browse the marketplace and click 'Book video tour' on any dog you'd like to see live before flying out."
            ctaLabel="Browse marketplace"
            ctaTo="/marketplace"
          />
        )}

        <div className="space-y-3">
          {tours.map((t) => {
            const meta = STATUS_META[t.status] || STATUS_META.pending_seller;
            const Icon = meta.icon;
            const isBuyer = user?.id === t.buyer_id;
            const isSeller = user?.id === t.seller_id;
            const counterparty = isBuyer ? t.seller : t.buyer;
            const cover = thumbUrl((t.dog?.photos || [])[0]);
            const proposed = t.scheduled_at || t.proposed_at;
            const proposedDate = proposed ? new Date(proposed) : null;
            return (
              <article
                key={t.id}
                className="rounded-xl border border-[#E5E5E5] bg-white p-4 flex gap-4 items-start"
                data-testid={`tour-row-${t.id}`}
              >
                <Link to={`/d/${t.dog?.id || ""}`} className="flex-shrink-0">
                  {cover ? (
                    <img src={cover} alt="" className="w-16 h-16 rounded-md object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded-md bg-[#FAFAFA] border border-[#E5E5E5] flex items-center justify-center text-[#8B8B8B]">
                      <VideoCamera size={20} weight="duotone" />
                    </div>
                  )}
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="font-spectral text-lg text-ink tracking-tight">
                      {t.dog?.name || "Dog"} · with {counterparty?.name || "—"}
                    </p>
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-full"
                      style={{ color: meta.color, background: meta.bg }}
                      data-testid={`tour-status-${t.id}`}
                    >
                      <Icon size={11} weight="fill" />
                      {meta.label}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[#6B6B6B] mt-1">
                    <span className="flex items-center gap-1">
                      <Clock size={11} />
                      {proposedDate ? proposedDate.toLocaleString() : "—"}
                    </span>
                    <span>15 min · ${t.price_usd}</span>
                    {t.message && (
                      <span className="flex items-center gap-1">
                        <ChatCircle size={11} /> "{t.message.slice(0, 80)}{t.message.length > 80 ? "…" : ""}"
                      </span>
                    )}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {/* Seller actions */}
                    {isSeller && t.status === "pending_seller" && (
                      <>
                        <Button
                          size="sm"
                          disabled={busyId === t.id}
                          onClick={() => respond(t.id, "accept")}
                          className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
                          data-testid={`tour-accept-${t.id}`}
                        >
                          <CheckCircle size={13} weight="duotone" className="mr-1" />
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === t.id}
                          onClick={() => proposeAlternatives(t.id)}
                          className="rounded-full"
                          data-testid={`tour-counter-${t.id}`}
                        >
                          <Calendar size={13} className="mr-1" />
                          Propose other times
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === t.id}
                          onClick={() => {
                            const reason = window.prompt("Why are you declining? (optional)");
                            if (reason !== null) respond(t.id, "decline", reason || "");
                          }}
                          className="rounded-full text-crimson border-[#C1272D] hover:bg-[#FDEEEE]"
                          data-testid={`tour-decline-${t.id}`}
                        >
                          Decline
                        </Button>
                      </>
                    )}

                    {/* Buyer — pick one of the seller's counter-proposed times */}
                    {isBuyer && t.status === "counter_proposed" && (
                      <div className="w-full" data-testid={`tour-counter-options-${t.id}`}>
                        {t.counter_message && (
                          <p className="text-[12px] text-[#3F3F3F] italic mb-2">"{t.counter_message}"</p>
                        )}
                        <p className="text-[11px] uppercase font-bold tracking-wider text-[#6B6B6B] mb-2">
                          Pick a time the seller offered:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {(t.counter_proposals || []).map((iso) => (
                            <Button
                              key={iso}
                              size="sm"
                              variant="outline"
                              disabled={busyId === t.id}
                              onClick={() => acceptCounter(t.id, iso)}
                              className="rounded-full text-[12px]"
                              data-testid={`tour-counter-pick-${t.id}-${iso}`}
                            >
                              {new Date(iso).toLocaleString()}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Seller — also see status of their own counter while buyer decides */}
                    {isSeller && t.status === "counter_proposed" && (
                      <p className="text-[12px] text-[#6B6B6B] italic">
                        Waiting on the buyer to pick one of your alternatives.
                      </p>
                    )}

                    {/* Buyer actions */}
                    {isBuyer && t.status === "pending_payment" && (
                      isIOS() ? (
                        <IosPurchaseNotice label="Tour booking payment" showPlans={false} />
                      ) : (
                      <Button
                        size="sm"
                        disabled={busyId === t.id}
                        onClick={() => setPayDialogTour(t)}
                        className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
                        data-testid={`tour-pay-${t.id}`}
                      >
                        <Coins size={13} weight="duotone" className="mr-1" />
                        Pay {tourPrice} to confirm
                      </Button>
                      )
                    )}

                    {/* Both — join when scheduled */}
                    {t.status === "paid_scheduled" && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => join(t.id)}
                          className="rounded-full bg-[#0F8043] hover:bg-[#0a6634] text-white"
                          data-testid={`tour-join-${t.id}`}
                        >
                          <VideoCamera size={13} weight="duotone" className="mr-1" />
                          Join tour
                          <ArrowRight size={11} className="ml-1" weight="bold" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadIcs(t.id)}
                          className="rounded-full text-[12px]"
                          data-testid={`tour-ics-${t.id}`}
                        >
                          <CalendarPlus size={13} className="mr-1" />
                          .ics
                        </Button>
                        {googleCalendarLink(t) && (
                          <a
                            href={googleCalendarLink(t)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded-full border border-[#E5E5E5] hover:border-ink px-3 h-8 text-[12px] text-ink"
                            data-testid={`tour-gcal-${t.id}`}
                          >
                            Add to Google Cal
                          </a>
                        )}
                      </>
                    )}

                    {/* Allow seller to propose new times BEFORE buyer pays even after they already accepted */}
                    {isSeller && t.status === "pending_payment" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => proposeAlternatives(t.id)}
                        className="rounded-full text-[12px] text-[#6B6B6B] hover:text-ink"
                        data-testid={`tour-counter-paypending-${t.id}`}
                      >
                        Need to reschedule?
                      </Button>
                    )}

                    {/* Cancel — open states only */}
                    {(t.status === "pending_seller" || t.status === "pending_payment" || t.status === "paid_scheduled") && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busyId === t.id}
                        onClick={() => cancel(t.id)}
                        className="rounded-full text-[#6B6B6B] hover:text-crimson"
                        data-testid={`tour-cancel-${t.id}`}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
      {payDialogTour && (
        <VideoTourPayDialog
          open={!!payDialogTour}
          tour={payDialogTour}
          tourPriceUsd={tourPriceUsd}
          onClose={(refresh) => {
            setPayDialogTour(null);
            if (refresh) load();
          }}
        />
      )}
    </AppShell>
  );
}
